import { Dict } from "@mp/core";
import { SceneComponent, ComponentInteractionType, ComponentOutput } from "@mp/common";
import { Object3D, MeshBasicMaterial, PlaneGeometry, Vector3, WebGLRenderTarget } from "three";
import { Mesh } from "three";
import { SceneEvents } from "../scenes/SceneEvents";
import { Events } from "phaser";

interface Inputs {
  sizeX: number,
  sizeY: number,
  color: number;
  lineColor: number;
  hoverOpacity: number;
  textureURL: string;
  showOnStep: number;
  visible: boolean;
}

type Outputs = {

} & ComponentOutput;

class OverlaySelector extends SceneComponent {
  private static caughtMarker: WebGLRenderTarget;
  private cameraPose: any = null;
  private cameraDirty = true;
  private opacity = 1;
  private hoverOpacity = 0.5;
  private root: Object3D|null = null;
  private sphere: Mesh|null = null;
  private checkPlane: Mesh|null = null;
  private sphereWP = new Vector3();
  private markerVisible = false;
  private hovered = false;
  private caught = false;
  private cameraSub: { cancel(): void }|null = null;
  private hintActiveTime = 0;
  private static hintDuration = 3000;

  inputs: Inputs = {
    sizeX: 1.0,
    sizeY: 1.0,
    color: 0xffff00,
    lineColor: 0x000000,
    hoverOpacity: 1,
    textureURL: "",
    showOnStep: null,
    visible: true,
  };

  outputs = {} as Outputs;

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  constructor(private eventBus: Events.EventEmitter, private sdk: any) {
    super();
  }

  onInit() {
    const THREE = this.context.three;
    if (!OverlaySelector.caughtMarker) {
      OverlaySelector.createCaughtMarker(THREE.WebGLRenderTarget);
    }
    this.root = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;

    this.makeSphere();

    this.onCameraPoseChanged = this.onCameraPoseChanged.bind(this);
    this.onHintActivated = this.onHintActivated.bind(this);
    this.cameraSub = this.sdk.Camera.pose.subscribe(this.onCameraPoseChanged);
    // this.eventBus.addListener(SceneEvents.DisplayHint, this.onHintActivated);
  }

  onEvent(eventType: string, eventData: Dict): void {
    if (eventType === ComponentInteractionType.CLICK) {
      this.sphere.getWorldPosition(this.sphereWP);
      this.notify(eventType, {
        position: this.sphereWP,
      });
      // show check after click
      const planeMat = this.checkPlane.material as MeshBasicMaterial;
      const sphereMat = this.sphere.material as MeshBasicMaterial;
      if (this.caught) {
        planeMat.opacity = 0;
        sphereMat.opacity = 0;
        this.caught = false;
      }  else {
        planeMat.opacity = 1;
        sphereMat.opacity = 1;
        this.caught = true;
      }
      // if the overlay was captured, we don't need the "hint" alwasys on top
      this.setAlwaysOnTop(true);

    }
    if (!this.caught) {
      if (eventType === ComponentInteractionType.HOVER) {
        this.hovered = eventData.hover
      }
    }
  }

  onTick() {
    // if (this.cameraDirty && this.cameraPose) {
    //   const target = this.cameraPose.position;
    //   this.root.lookAt(target.x, target.y, target.z);
    //   this.cameraDirty = false;
    // }

    if (!this.inputs.showOnStep) {
      const hintActive = this.hintActiveTime + OverlaySelector.hintDuration > Date.now();
      const markerVisible = this.hovered || hintActive;
      if (this.markerVisible !== markerVisible) {
        const sphereMat = this.sphere.material as MeshBasicMaterial;
        sphereMat.opacity = !this.caught && markerVisible ? this.hoverOpacity : 0.2;

        const planeMat = this.checkPlane.material as MeshBasicMaterial;
        planeMat.opacity = this.caught && markerVisible ? this.hoverOpacity : 0.2;
        this.setAlwaysOnTop(true);
      } else {
        this.setAlwaysOnTop(false);
      }
      if (!this.caught && hintActive) {
        // this.setAlwaysOnTop(true);
      }
      this.markerVisible = markerVisible;
    } else {
      this.setAlwaysOnTop(true);
    }
  }

  onDestroy() {
    if (this.sphere) {
      this.sphere.geometry.dispose();
      (this.sphere.material as MeshBasicMaterial).dispose();
    }
    this.cameraSub.cancel();
    // this.eventBus.removeListener(SceneEvents.DisplayHint, this.onHintActivated);
  }

  private makeSphere() {
    const THREE = this.context.three;

    if (this.sphere) {
      this.root.remove(this.sphere);
      (this.sphere.material as MeshBasicMaterial).dispose();
      (this.sphere.geometry as PlaneGeometry).dispose();
      this.sphere = null;
    }

    const sphereGeometry = new THREE.PlaneGeometry(this.inputs.sizeX, this.inputs.sizeY);
    const texture = new THREE.TextureLoader().load( this.inputs.textureURL );

    let defaultOpacity = this.opacity;
    if (typeof this.inputs.showOnStep === "undefined" || this.inputs.showOnStep === null) {
      defaultOpacity = 0.2;
    } else {
      defaultOpacity = 1;
    }

    var sphereMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: defaultOpacity,
      map: texture,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: true,
    });

    this.sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this.root.add(this.sphere);

    const planeSize = 0.5 * this.inputs.sizeX;
    this.checkPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(planeSize, planeSize),
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        color: 0x00ff00,
        map: OverlaySelector.caughtMarker.texture,
        side: THREE.DoubleSide,
        depthTest: true,
        depthWrite: true,
      }));
    this.checkPlane.position.z = this.inputs.sizeX;
    this.sphere.renderOrder = 10;
    this.checkPlane.renderOrder = 11;

    this.root.add(this.checkPlane);
  }

  private static createCaughtMarker(renderTargetFactory: typeof WebGLRenderTarget) {
    const renderTargetSize = 64;
    const canvas = document.createElement('canvas');
    const renderContext2D = canvas.getContext('2d');
    canvas.width = renderTargetSize;
    canvas.height = renderTargetSize;


    renderContext2D.textAlign = 'center';
    renderContext2D.textBaseline = 'middle';
    renderContext2D.fillStyle = '#fff';
    renderContext2D.font = renderTargetSize + 'px Roboto';
    renderContext2D.fillText('', 0.5 * renderTargetSize, 0.5 * renderTargetSize);

    // create three.js objects to render
    this.caughtMarker = new renderTargetFactory(renderTargetSize, renderTargetSize);
    this.caughtMarker.texture.image = renderContext2D.canvas;
    this.caughtMarker.texture.needsUpdate = true;
  }

  private onCameraPoseChanged(pose: any) {
    this.cameraPose = pose;
    this.cameraDirty = true;
  }

  private onHintActivated() {
    this.hintActiveTime = Date.now();
  }

  private setAlwaysOnTop(onTop: boolean) {
    const sphereMat = this.sphere.material as MeshBasicMaterial;
    sphereMat.depthTest = !onTop;
    sphereMat.depthWrite = !onTop;

    const planeMat = this.checkPlane.material as MeshBasicMaterial;
    planeMat.depthTest = !onTop;
    planeMat.depthWrite = !onTop;
  }
}

export const overlaySelectorSphereType = 'guided.overlayselector';

export const createOverlaySelectorClosure = function(eventBus: Events.EventEmitter, sdk: any) {
  return function() {
    return new OverlaySelector(eventBus, sdk);
  };
}
